import sha1 from '/fp/sha1.js';
import { pushCollectIncident } from '/fp/collect-incidents.js';
import {
  startCollectPrewarm,
  getCpuBenchmark,
  getStorageProbes,
  getAudioFingerprint,
  getCollectPrewarmHits,
  takeCollectPrewarmIncidents,
  getRenderResult,
  renderProbeNames,
} from '/fp/prewarm/collect-prewarm.js';
import { compute_dom_rect } from '/fp/canvas.js';
import { isAndroid, isChromiumAndroid } from '/fp/platform.mjs';
import {
  mergeFpOptions,
  resolveStorageTimeoutMs,
  triState,
  resolveProducts,
  resolveMode,
  resolveAutoCollect,
  isSdkMode,
  SDK_MODE,
} from '/fp/options.mjs';
import { mergeSubmitData, SUBMIT_DEVICE_WAIT_MS } from '/fp/device-cache.mjs';
import { shouldSplitSubmit, SUBMIT_XHR_MAX_BODY } from '/fp/submit-body.mjs';
import { deviceHint } from '/fp/device-hint.mjs';
import { withTimeout } from '/fp/prewarm/with-timeout.mjs';
import { hasFreshDeviceData, loadDeviceData, saveDeviceData, takeStoredTiming } from '/fp/device-store.js';
import { stripProductFields } from '/fp/product-filter.mjs';
import { isOpfsSupported } from '/fp/persistent-storage.js';
import { startPrewarm, getLocalMarker, getGlobalMarker, getPrewarmTimings, LOCAL_FALLBACK } from '/fp/prewarm/marker-prewarm.js';
import getPlugins from '/fp/plugins.js';
import collectBrowserOther, { getMimeTypes } from '/fp/browser.js';
import getFeaturesFP, { primeSpeechVoices } from '/fp/features.js';
import getNavigatorData from '/fp/navigator.js';
import getJavascriptData from '/fp/javascript.js';
import collectAutomationSignals from '/fp/automation.js';
import {
  ensureRsSessionId,
  pinRsSessionId,
  readLastCollect,
  recentCollectSessionId,
  writeLastCollect,
  getApiHost,
  sdkPostUrl,
  postWithRetry,
  supportsKeepalive,
  KEEPALIVE_MAX_BODY,
  encryptBody,
  collectTimeoutMs,
  withTiming,
  reportSdkCollectFailure,
  sdkConfigForCollect,
  invokeCallback,
} from '/utils.js';
import { SDK_ERROR, SDK_WARNING, errorCodeFrom } from '/errors.js';
import mediaQueries from '/fp/media-queries.js';
import LZString from '/fp/compress.js';
import { getAudioFormats, getVideoFormats } from '/fp/media.js';
import { getEncryptedMediaCapabilities } from '/fp/drm.js';
import getExtensions, { shouldCollectExtensions } from '/fp/extensions.js';
import getPerformanceBenchmarks, { collectWorkerNavigator } from '/fp/performance.js';
import collectWebGpu from '/fp/webgpu.js';
import config from '/config.js';

const BUNDLE_SRC =
  typeof document !== 'undefined' && document.currentScript && document.currentScript.src
    ? document.currentScript.src
    : null;

function allKeyed(jobs) {
  const keys = Object.keys(jobs);
  return Promise.all(keys.map((k) => jobs[k])).then((values) => {
    const out = {};
    for (let i = 0; i < keys.length; i++) out[keys[i]] = values[i];
    return out;
  });
}

function isolateWith(timings, incidents) {
  return (name, fallback, fn) => {
    try {
      const v = withTiming(name, timings, fn);
      if (v && typeof v.then === 'function') {
        return v.then(
          (x) => x,
          (err) => {
            pushCollectIncident(incidents, { source: name, phase: 'collector', error: err });
            return fallback;
          }
        );
      }
      return v;
    } catch (err) {
      pushCollectIncident(incidents, { source: name, phase: 'collector', error: err });
      return fallback;
    }
  };
}

function sendKeepalive(url, payload, json, timeoutMs, dispatch) {
  const onDispatched = dispatch && typeof dispatch.onDispatched === 'function' ? dispatch.onDispatched : null;
  let released = false;
  const release = function () {
    if (released || !onDispatched) return;
    released = true;
    onDispatched();
  };
  const timer = onDispatched ? setTimeout(release, dispatch.releaseAfterMs) : null;
  const onXhrRetry = function () {
    if (timer !== null) clearTimeout(timer);
  };
  return postWithRetry(url, payload, timeoutMs, 'submit', { keepaliveJson: json, onXhrRetry: onXhrRetry }).then(
    function (res) {
      if (timer !== null) clearTimeout(timer);
      release();
      return res;
    },
    function (err) {
      if (timer !== null) clearTimeout(timer);
      throw err;
    }
  );
}

function postSubmitBody(url, payload, keepalive, timeoutMs, dispatch) {
  const json = keepalive ? JSON.stringify(payload) : null;
  return json !== null && json.length <= KEEPALIVE_MAX_BODY
    ? sendKeepalive(url, payload, json, timeoutMs, dispatch)
    : postWithRetry(url, payload, timeoutMs, 'submit');
}

const AUTOMATION_FALLBACK = { stackCdp: false };

function currentPageUrl() {
  return window.location.href !== null && window.location.href !== undefined ? window.location.href : '';
}

function resolveFpOptions(options) {
  return mergeFpOptions(config.fpDefaults, options && options.fp);
}

function deviceScope(appId, options) {
  return { appId, products: resolveProducts(options), fpOptions: resolveFpOptions(options) };
}

function resolveGpuFlag(fp) {
  const flag = triState(fp.gpuBenchmark);
  if (flag !== null) return flag;
  return !isAndroid();
}

function storageProbeFallback() {
  let opfsSupported;
  try {
    opfsSupported = isOpfsSupported();
  } catch (e) {
    opfsSupported = undefined;
  }
  return { storage: opfsSupported === undefined ? {} : { opfsSupported } };
}

function bundleTiming() {
  try {
    if (!BUNDLE_SRC || typeof performance.getEntriesByName !== 'function') return null;
    const entry = performance.getEntriesByName(BUNDLE_SRC)[0];
    if (!entry) return null;
    return {
      duration: Math.round(entry.duration),
      transferSize: typeof entry.transferSize === 'number' && entry.transferSize > 0 ? entry.transferSize : null,
    };
  } catch (e) {
    return null;
  }
}

function addPreconnect(base) {
  if (typeof document === 'undefined' || !document.head) return;
  let origin;
  try {
    origin = new URL(base).origin;
  } catch (e) {
    return;
  }
  if (document.head.querySelector('link[rel="preconnect"][href="' + origin + '"]')) return;
  const rels = ['preconnect', 'dns-prefetch'];
  for (let i = 0; i < rels.length; i++) {
    const link = document.createElement('link');
    link.rel = rels[i];
    link.href = origin;
    if (rels[i] === 'preconnect') link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
}

class RiskSealClient {
  appId = '';
  browserId = '';
  params = {};
  sessionId = null;

  os = {};
  browserBrands = {};

  /**
   * @param {string} appId Public UUID in collect URL and AES key derivation.
   * @param {{ endpoints?: string[], timeout?: number, debug?: boolean, fp?: { extensions?: boolean|'auto'|'on'|'off', prewarm?: boolean, cpuBenchPrewarm?: boolean|'auto'|'on'|'off', renderPrewarm?: boolean|'auto'|'on'|'off', gpuBenchmark?: boolean|'auto'|'on'|'off', storageTimeoutMs?: number|'auto', androidStorageTimeoutMs?: number, preconnect?: boolean } }} [options] Optional overrides (merged from rsSDK('config', ...)). Non-empty `endpoints` uses the first non-empty entry as collect base; otherwise `rsSDK.defaultEndpoint`. `timeout` is collect XHR ms (finite number only; default 10000). `fp.*` defaults live in config.js `fpDefaults`; semantics in sdk/README.md.
   */
  constructor(appId, options) {
    this.appId = appId;
    this.options = options || {};
  }

  #postUrl(path) {
    const base = getApiHost(this.options, config.defaultEndpoint);
    const pathSeg = String(path).replace(/^\//, '');
    return sdkPostUrl(base, pathSeg, this.appId.trim());
  }

  #collectUrl() {
    return this.#postUrl(config.collectPath);
  }

  #submitUrl() {
    return this.#postUrl(config.submitPath);
  }

  /** XHR timeout for collect and error-report POSTs (ms). */
  #collectTimeoutMs() {
    return collectTimeoutMs(this.options);
  }

  #waitForDomReady() {
    if (typeof document === 'undefined') return Promise.resolve();
    if (document.readyState !== 'loading') return Promise.resolve();
    return new Promise(function (resolve) {
      function onReady() {
        document.removeEventListener('DOMContentLoaded', onReady);
        resolve();
      }
      document.addEventListener('DOMContentLoaded', onReady);
    });
  }

  async #reportCollectFailure(err, sessionId, reportContext, delivery) {
    await reportSdkCollectFailure(
      Object.assign(
        {
          appId: this.appId,
          url: this.#collectUrl(),
          timeoutMs: this.#collectTimeoutMs(),
          err,
          sessionId,
          reportContext,
        },
        delivery
      )
    );
  }

  async #buildRequest(params = {}, collectTs = performance.now()) {
    const startTime = performance.now();

    let data = {
      version: __SDK_VERSION__,
      fp: {},
      markers: {},
      media: {},
      highEntropyValues: {},
      browser: {},
      automation: {},
      incidents: [],
      resistance: {},
      timing: {},
      performance: {},
      webWorker: null,
      params: params,
      url: currentPageUrl(),
    };

    let audioFp, webglDetail, webgl2Detail, webgpuDetail, resistanceResult;
    const timings = {};

    const collectorIncidents = data.incidents;
    const isolate = isolateWith(timings, collectorIncidents);

    const products = resolveProducts(this.options);
    const fp = resolveFpOptions(this.options);
    const prewarmHits = getCollectPrewarmHits();
    let storageProbe = 'ok';
    const storageTimeoutMs = resolveStorageTimeoutMs(fp, isChromiumAndroid(), fp.androidStorageTimeoutMs);
    const globalMarkerPromise = products.fp
      ? isolate('markerGlobal', null, () => getGlobalMarker())
      : Promise.resolve(null);
    const prewarmTimings = getPrewarmTimings(collectTs);
    const markersPromise = products.fp
      ? isolate('markers', LOCAL_FALLBACK, () => getLocalMarker())
      : Promise.resolve(null);
    const storageCapMs = markersPromise.then((m) => (m && m.markers && m.markers.new === false ? storageTimeoutMs : 0));
    const storageProbePromise = isolate('storageStabilizeIndexedDB', storageProbeFallback(), () =>
      getStorageProbes(storageCapMs, storageProbeFallback).then(
        (r) => {
          if (r.timedOut) storageProbe = 'timeout';
          return r.value;
        },
        (err) => {
          storageProbe = 'error';
          throw err;
        }
      )
    );
    const collectExtensions = products.fp && shouldCollectExtensions({ fp });
    const runGpu = products.di && resolveGpuFlag(fp);
    const jobs = {};
    if (products.di) {
      jobs.performance = isolate('performance', null, () =>
        getPerformanceBenchmarks({ cpu: getCpuBenchmark(), gpu: runGpu })
      );
    } else if (products.fp) {
      jobs.webWorkerProbe = isolate('webWorkerProbe', null, () => collectWorkerNavigator());
    }
    jobs.highEntropyValues = isolate('highEntropyValues', null, () => this.collectBrowserData());
    if (products.fp) jobs.audio = isolate('audio', null, () => getAudioFingerprint());
    jobs.browser = isolate('browser', {}, async () => {
      const browserResult = await this.getBrowserData({ sensors: products.di });
      timings.browserDetails = browserResult.details;
      return {
        plugins: browserResult.plugins,
        mimeTypes: browserResult.mimeTypes,
        other: browserResult.other,
      };
    });
    if (products.di) jobs.webgpu = isolate('webgpu', null, () => collectWebGpu());
    if (products.fp) {
      jobs.fonts = isolate('fonts', null, () => getRenderResult('fonts'));
      jobs.resistanceCanvas = isolate('resistanceCanvas', { canvas: null }, () => getRenderResult('resistanceCanvas'));
    }
    jobs.javascript = isolate('javascript', {}, () => getJavascriptData({ battery: products.di }));
    jobs.features = isolate('features', {}, () =>
      getFeaturesFP({ voices: products.fp, performance: products.fp, math: products.fp })
    );
    if (collectExtensions) jobs.extensions = isolate('extensions', null, () => getExtensions());
    jobs.navigator = isolate('navigator', {}, () => getNavigatorData());
    jobs.automation = isolate('automation', AUTOMATION_FALLBACK, () => collectAutomationSignals());
    jobs.mediaQueries = isolate('mediaQueries', {}, () => mediaQueries());
    if (products.fp) {
      jobs.audioFormats = isolate('audioFormats', null, () => getAudioFormats());
      jobs.videoFormats = isolate('videoFormats', null, () => getVideoFormats());
      jobs.drm = isolate('drm', null, () => getEncryptedMediaCapabilities());
      jobs.domRect = isolate('domRect', null, () => compute_dom_rect(collectorIncidents));
    }
    jobs.fontCanvas = isolate('fontCanvas', null, () => getRenderResult('fontCanvas'));
    if (products.fp) {
      jobs.cpuCanvas = isolate('cpuCanvas', null, () => getRenderResult('cpuCanvas'));
      jobs.gpuCanvas = isolate('gpuCanvas', null, () => getRenderResult('gpuCanvas'));
    }
    jobs.webgl = isolate('webgl', null, () => getRenderResult('webgl'));
    jobs.webgl2 = isolate('webgl2', null, () => getRenderResult('webgl2'));

    const [markersResult, storageProbeResult, r, globalMarker] = await Promise.all([
      markersPromise,
      storageProbePromise,
      allKeyed(jobs),
      globalMarkerPromise,
    ]);
    collectorIncidents.push(...takeCollectPrewarmIncidents());

    data.performance = products.di ? r.performance : null;
    if (jobs.webWorkerProbe) data.webWorker = r.webWorkerProbe;
    data.highEntropyValues = r.highEntropyValues;
    audioFp = r.audio;
    data.browser = r.browser;
    webgpuDetail = r.webgpu;
    if (products.fp) data.fp.fonts = r.fonts;
    resistanceResult = r.resistanceCanvas;
    data.javascript = r.javascript;
    data.features = r.features;
    if (collectExtensions) data.extensions = r.extensions;
    data.navigator = r.navigator;
    data.automation = r.automation;
    data.mediaQueries = r.mediaQueries;
    if (products.fp) {
      data.media.audioFormats = r.audioFormats;
      data.media.videoFormats = r.videoFormats;
      data.media.drm = r.drm;
      data.fp.domRect = r.domRect;
      data.fp.cpuCanvas = r.cpuCanvas;
      data.fp.gpuCanvas = r.gpuCanvas;
    }
    data.fp.fontCanvas = r.fontCanvas;
    webglDetail = r.webgl;
    webgl2Detail = r.webgl2;

    if (markersResult) {
      data.markers = markersResult.markers;
      timings.markersDetails = markersResult.details;
    }
    if (globalMarker) data.markerGlobal = globalMarker;
    timings.storageStabilizeDirectory = timings.storageStabilizeIndexedDB;

    if (products.fp) data.resistance.canvas = resistanceResult?.canvas ?? null;

    if (Array.isArray(data.navigator?.incidents) && data.navigator.incidents.length) {
      collectorIncidents.push(...data.navigator.incidents);
    }
    if (Array.isArray(data.browser?.other?.incidents) && data.browser.other.incidents.length) {
      collectorIncidents.push(...data.browser.other.incidents);
    }

    data.features = { ...data.features, storage: storageProbeResult.storage };
    if (storageProbeResult.indexedDB !== undefined) data.features.indexedDB = storageProbeResult.indexedDB;
    if (storageProbeResult.fileSystem !== undefined) data.features.fileSystem = storageProbeResult.fileSystem;

    if (products.fp) {
      if (typeof audioFp?.hash != 'undefined') {
        data.fp.audio = {
          hash: sha1(audioFp.hash),
        };
      } else {
        data.fp.audio = {};
      }
    }

    if (webglDetail) {
      // Exclude `incidents` from the hashed keys — it is diagnostics, not a fingerprint signal.
      // Hashing it would drift the webgl hash whenever a probe fails and break similarity matching.
      const webglHashKeys = Object.keys(webglDetail)
        .filter((k) => k !== 'incidents')
        .sort();
      data.fp.webgl = {
        hash: sha1(JSON.stringify(webglDetail, webglHashKeys)),
        details: { unmaskedRendererInfo: webglDetail.unmaskedRendererInfo },
      };
    } else {
      data.fp.webgl = null;
    }

    if (webgl2Detail) {
      const webgl2HashKeys = Object.keys(webgl2Detail)
        .filter((k) => k !== 'incidents')
        .sort();
      data.fp.webgl2 = {
        hash: sha1(JSON.stringify(webgl2Detail, webgl2HashKeys)),
        details: { unmaskedRendererInfo: webgl2Detail.unmaskedRendererInfo },
      };
    } else {
      data.fp.webgl2 = null;
    }

    if (products.di) data.fp.webgpu = webgpuDetail;

    let gpuBenchmark = runGpu ? 'failed' : 'off';
    if (data.performance) {
      data.webWorker = data.performance.webWorker ?? null;
      if (data.performance.webWorkerMs != null) {
        timings.webWorker = data.performance.webWorkerMs;
      }
      const perfErrors = data.performance.errors || {};
      if (perfErrors.cpu)
        pushCollectIncident(collectorIncidents, { source: 'performance', phase: 'cpu', error: perfErrors.cpu });
      if (perfErrors.gpu)
        pushCollectIncident(collectorIncidents, { source: 'performance', phase: 'gpu', error: perfErrors.gpu });
      gpuBenchmark = data.performance.gpuSkipped ? 'off' : perfErrors.gpu ? 'failed' : 'run';
      data.performance = {
        cpu: data.performance.cpu,
        gpu: data.performance.gpu,
      };
    }
    if (audioFp && typeof audioFp.retries === 'number') timings.audioRetries = audioFp.retries;

    if (!products.fp) {
      delete data.markers;
      delete data.media;
      delete data.resistance;
    }
    if (!products.di) {
      delete data.performance;
    }
    stripProductFields(data, products);

    const endTime = performance.now();
    data.timing = {
      ...timings,
      total: endTime - startTime,
      markerPrewarmLeadMs: prewarmTimings.markerPrewarmLeadMs,
      sdkLoadToCollectMs: prewarmTimings.sdkLoadToCollectMs,
      markerPrewarmHit: prewarmTimings.markerPrewarmHit,
      markerGlobalPrewarmHit: prewarmTimings.markerGlobalPrewarmHit,
      cpuBenchPrewarmHit: prewarmHits.cpuBenchPrewarmHit,
      storagePrewarmHit: prewarmHits.storagePrewarmHit,
      audioPrewarmHit: prewarmHits.audioPrewarmHit,
      renderPrewarmHits: prewarmHits.renderPrewarmHits,
      renderPrewarmStarted: prewarmHits.renderPrewarmStarted,
      storageProbe,
      gpuBenchmark,
      bundle: bundleTiming(),
    };

    return data;
  }

  async #buildFresh(params = {}) {
    const timings = {};
    const incidents = [];
    const isolate = isolateWith(timings, incidents);
    const products = resolveProducts(this.options);
    const jobs = {};
    if (products.fp) {
      jobs.markers = isolate('markers', LOCAL_FALLBACK, () => getLocalMarker());
      jobs.markerGlobal = isolate('markerGlobal', null, () => getGlobalMarker());
    }
    jobs.automation = isolate('automation', AUTOMATION_FALLBACK, () => collectAutomationSignals());
    const r = await allKeyed(jobs);

    const data = {
      automation: r.automation,
      params: params,
      url: currentPageUrl(),
      incidents: incidents,
      timing: timings,
    };
    if (r.markers) {
      data.markers = r.markers.markers;
      timings.markersDetails = r.markers.details;
    }
    if (r.markerGlobal) data.markerGlobal = r.markerGlobal;
    return data;
  }

  /** @returns {Promise<object>} Collected fingerprint payload (plain object). Use LZString.compressToBase64(JSON.stringify(...)) for the wire `sdk` field. */
  async getRequest(params = {}) {
    return this.#buildRequest(params);
  }

  ensureSessionId() {
    if (this.sessionId) return this.sessionId;
    this.sessionId = ensureRsSessionId();
    return this.sessionId;
  }

  async collect() {
    if (!__SDK_COLLECT_ALL_THE_TIME__) {
      const recentSessionId = recentCollectSessionId(readLastCollect(), Date.now());
      if (recentSessionId) {
        this.sessionId = pinRsSessionId(recentSessionId);
        return { session_id: recentSessionId };
      }
    }
    return this.#collectThen(async (sdkPlain, sessionId, products, setPhase) => {
      setPhase('encrypt');
      const sdkCompressed = LZString.compressToBase64(JSON.stringify(sdkPlain));
      const data = {
        sdk: sdkCompressed,
        session_id: sessionId,
        version: sdkPlain.version ?? null,
        features: { fp: products.fp, di: products.di },
      };
      const payload = await encryptBody(this.appId, data);
      setPhase('post');
      const url = this.#collectUrl();
      await postWithRetry(url, payload, this.#collectTimeoutMs(), 'collect');
      writeLastCollect(sessionId);
    });
  }

  async collectToCache() {
    return this.#collectThen(async (sdkPlain, sessionId, products, setPhase) => {
      setPhase('store');
      await saveDeviceData(deviceScope(this.appId, this.options), sdkPlain);
    }, 'submit');
  }

  async #collectThen(deliver, source) {
    const collectTs = performance.now();
    const debug = this.options?.debug ?? false;
    let sessionId = null;
    let phase = 'dom_wait';
    let fpTiming = null;
    try {
      await this.#waitForDomReady();
      await new Promise(function (r) {
        setTimeout(r, 0);
      });
      this.ensureSessionId();
      const products = resolveProducts(this.options);
      sessionId = this.sessionId;
      phase = 'fingerprint';
      const sdkPlain = await this.#buildRequest({}, collectTs);
      fpTiming = sdkPlain.timing;
      await deliver(sdkPlain, sessionId, products, function (next) {
        phase = next;
      });
      return { session_id: sessionId };
    } catch (err) {
      if (debug) console.warn('[rsSDK]', err);
      const reportContext = {
        phase: phase,
        fp_timing: fpTiming,
      };
      if (source) reportContext.source = source;
      void this.#reportCollectFailure(err, sessionId, reportContext);
      return {
        session_id: null,
        error: errorCodeFrom(err, SDK_ERROR.COLLECT_FAILED),
      };
    }
  }

  readFreshPart() {
    return this.#buildFresh({}).then(
      function (data) {
        return { data, error: null };
      },
      function (error) {
        return { data: null, error };
      }
    );
  }

  takeBehaviourSnapshot(bb) {
    if (!bb) return { snapshot: null, error: null };
    try {
      return { snapshot: bb.snapshot(this.appId, this.ensureSessionId()), error: null };
    } catch (err) {
      return { snapshot: null, error: err };
    }
  }

  async submit({ device, deviceError, deviceRequested, fresh, bb, behaviour, dispatch, storedDevice }) {
    const debug = this.options?.debug ?? false;
    const sessionId = this.ensureSessionId();
    const snapshot = behaviour.snapshot;
    const failedParts = behaviour.error ? [{ phase: 'bb', err: behaviour.error }] : [];
    if (deviceError) failedParts.push({ phase: 'device', err: deviceError });
    let phase = 'compress';
    let sdkPlain = null;
    let result;
    let behaviourResult = behaviour.error ? { session_id: null, error: SDK_ERROR.BB_FAILED } : null;
    let restoreTiming = null;
    if (device) {
      try {
        const read = await fresh;
        if (read.error) throw read.error;
        sdkPlain = mergeSubmitData(device.data, read.data, {
          cacheHit: device.stored === true,
          ageMs: Math.max(0, Date.now() - device.savedAt),
        });
      } catch (err) {
        failedParts.push({ phase: 'fresh', err });
      }
    }
    if (!sdkPlain && !snapshot) {
      let error = SDK_ERROR.NOTHING_TO_SUBMIT;
      if (failedParts.length) {
        error = SDK_ERROR.SUBMIT_FAILED;
      } else if (deviceRequested) {
        error = SDK_ERROR.NO_DEVICE_DATA;
        failedParts.push({ phase: 'device', err: new Error('device data not ready') });
      }
      result = { session_id: null, error };
    } else {
      try {
        const products = resolveProducts(this.options);
        const data = {
          session_id: sessionId,
          version: __SDK_VERSION__ ?? null,
          features: { fp: products.fp, di: products.di },
          config: sdkConfigForCollect(this.options),
        };
        const hint = snapshot ? deviceHint(sdkPlain || (device || storedDevice || {}).data) : null;
        if (sdkPlain) data.sdk = LZString.compressToBase64(JSON.stringify(sdkPlain));
        else if (hint) data.device_hint = hint;
        if (snapshot) data.bb = snapshot.payload;
        if (sdkPlain) restoreTiming = takeStoredTiming(deviceScope(this.appId, this.options), device);
        const sent = await this.#sendSubmit(data, sdkPlain ? hint : null, sessionId, dispatch, function (next) {
          phase = next;
        });
        result = { session_id: sessionId };
        if (deviceRequested && !sent.device) result.warning = SDK_WARNING.NO_DEVICE_DATA;
        else if ((snapshot && !sent.behaviour) || behaviour.error) result.warning = SDK_WARNING.NO_BEHAVIOUR_DATA;
        if (!sent.device && restoreTiming) restoreTiming();
        if (sent.behaviourError) {
          behaviourResult = { session_id: null, error: errorCodeFrom(sent.behaviourError, SDK_ERROR.SUBMIT_FAILED) };
        }
      } catch (err) {
        if (restoreTiming) restoreTiming();
        if (debug) console.warn('[rsSDK]', err);
        await this.#reportSubmitFailure(err, sessionId, phase);
        result = { session_id: null, error: errorCodeFrom(err, SDK_ERROR.SUBMIT_FAILED) };
      }
    }
    for (let i = 0; i < failedParts.length; i++) {
      if (debug) console.warn('[rsSDK]', failedParts[i].err);
      void this.#reportSubmitFailure(failedParts[i].err, sessionId, failedParts[i].phase);
    }
    if (snapshot || behaviour.error) bb.afterSubmit(this.appId, sessionId, snapshot, behaviourResult || result);
    return result;
  }

  async #sendSubmit(data, hint, sessionId, dispatch, setPhase) {
    const url = this.#submitUrl();
    const timeoutMs = this.#collectTimeoutMs();
    const keepalive = supportsKeepalive();
    setPhase('encrypt');
    if (!shouldSplitSubmit(data, keepalive ? KEEPALIVE_MAX_BODY : SUBMIT_XHR_MAX_BODY)) {
      const whole = await encryptBody(this.appId, data);
      setPhase('post');
      await postSubmitBody(url, whole, keepalive, timeoutMs, dispatch);
      return { device: !!data.sdk, behaviour: !!data.bb, behaviourError: null };
    }
    const { sdk, ...behaviourPart } = data;
    const behaviourOnly = hint ? { ...behaviourPart, device_hint: hint } : behaviourPart;
    const { bb, ...deviceOnly } = data;
    const settle = function (send) {
      return send.then(
        function () {
          return null;
        },
        function (err) {
          return err;
        }
      );
    };
    const sendBehaviour = encryptBody(this.appId, behaviourOnly).then(function (payload) {
      setPhase('post');
      return postSubmitBody(url, payload, keepalive, timeoutMs, null);
    });
    const sendDevice = encryptBody(this.appId, deviceOnly).then(function (payload) {
      return postWithRetry(url, payload, timeoutMs, 'submit');
    });
    const [behaviourError, deviceError] = await Promise.all([settle(sendBehaviour), settle(sendDevice)]);
    if (deviceError) void this.#reportSubmitFailure(deviceError, sessionId, 'post');
    if (behaviourError && deviceError) throw behaviourError;
    if (behaviourError) void this.#reportSubmitFailure(behaviourError, sessionId, 'post');
    return { device: !deviceError, behaviour: !behaviourError, behaviourError };
  }

  #reportSubmitFailure(err, sessionId, phase) {
    return this.#reportCollectFailure(
      err,
      sessionId,
      { phase: phase, source: 'submit' },
      { failLabel: 'submit', preferBeacon: true }
    );
  }

  async getBrowserData(opts) {
    const details = {};
    const otherSteps = {};
    details.other = otherSteps;

    const [plugins, mimeTypes, other] = await Promise.all([
      withTiming('plugins', details, () => getPlugins()),
      withTiming('mimeTypes', details, () => getMimeTypes()),
      withTiming('total', otherSteps, () => collectBrowserOther(otherSteps, opts)),
    ]);

    return { plugins, mimeTypes, other, details };
  }

  async getJsonData(url) {
    try {
      const response = await this.fetchWithTimeout(url);
      const data = await response.json();
      return data;
    } catch (error) {
      if (this.options.debug) {
        console.warn('[rsSDK]', error);
      }
    }
    return {};
  }

  async fetchWithTimeout(resource, options = {}) {
    const { timeout = 1000 } = options;

    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    const response = await fetch(resource, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    return response;
  }

  collectBrowserData() {
    // this is for desktop, for iPhone or old browsers fallback to UserAgent or other methods.
    if (navigator.userAgentData !== undefined && navigator.userAgentData.getHighEntropyValues !== undefined) {
      return navigator.userAgentData.getHighEntropyValues([
        'architecture',
        'bitness',
        'formFactors',
        'fullVersionList',
        'model',
        'platformVersion',
        'uaFullVersion',
        'wow64',
      ]);
    } else {
      return new Promise((resolve, reject) => resolve(null));
    }
  }
}

/**
 * Build the `rsSDK` callable.
 *
 * @param {{ createBbCommands?: (api: { getAppId: () => string, getOptions: () => object, getSubmit: () => Function | null }) => { collect: Function, debug?: Function, snapshot: Function, afterSubmit: Function, holdSubmitTimeoutMs: () => number }, applyBbFromConfig?: (api: { rsSDK: Function, options: object, appId: string }) => void }} [features]
 *   Optional feature factories. When `createBbCommands` is provided, `bb:collect` and `bb:debug` are wired.
 * @returns {Function} The `rsSDK` function with `version` and `defaultEndpoint` properties.
 */
export function createRsSDK(features = {}) {
  /** Async stub queue before bundle loads: rsSDK.q.push([].slice.call(arguments)) */
  const _rsSdkPrev = typeof window !== 'undefined' ? window.rsSDK : undefined;
  const _rsSdkQueued = _rsSdkPrev && _rsSdkPrev.q && _rsSdkPrev.q.slice ? _rsSdkPrev.q.slice() : [];

  let _defaultAppId = '';
  let _defaultOptions = {};
  let _collectInFlight = null;
  let _configApplied = false;
  let _mode = SDK_MODE.LEGACY;
  let _autoCollect = { auto: false, delayMs: 0 };
  let _autoCollectTimer = null;
  let _submitInFlight = null;
  let _submitDispatched = null;
  let _collectFailed = false;

  const bbCommands =
    typeof features.createBbCommands === 'function'
      ? features.createBbCommands({
          getAppId: () => _defaultAppId,
          getOptions: () => _defaultOptions,
          getSubmit: () => (_mode === SDK_MODE.DEFAULT ? runSubmit : null),
        })
      : null;

  function rsSDK(command, arg2, arg3) {
    if (command === 'config') {
      if (_configApplied) return;
      let appId = arg2;
      let opts = arg3;
      if (typeof appId !== 'string' || !appId.trim()) {
        if (_defaultOptions.debug) console.warn('[rsSDK] config: invalid appId');
        return;
      }
      _defaultAppId = appId.trim();
      _defaultOptions =
        opts && typeof opts === 'object' ? Object.assign({}, opts, { debug: !!(opts && opts.debug) }) : {};
      _configApplied = true;
      const fp = resolveFpOptions(_defaultOptions);
      const products = resolveProducts(_defaultOptions);
      _mode = resolveMode(_defaultOptions);
      if (_defaultOptions.mode != null && !isSdkMode(_defaultOptions.mode)) {
        const given =
          typeof _defaultOptions.mode === 'string' ? "'" + _defaultOptions.mode + "'" : String(_defaultOptions.mode);
        console.warn(
          '[rsSDK] config: invalid mode ' + given + ". Allowed values are 'legacy' and 'default'. Using 'legacy'."
        );
      }
      const deviceCached = _mode === SDK_MODE.DEFAULT && (products.fp || products.di) && hasCachedDevice();
      if (!deviceCached) startHeavyPrewarm();
      if (products.fp) {
        try {
          startPrewarm();
        } catch (e) {}
      }
      if (triState(fp.preconnect) !== false) {
        try {
          addPreconnect(getApiHost(_defaultOptions, config.defaultEndpoint));
        } catch (e) {}
      }
      if (bbCommands && typeof features.applyBbFromConfig === 'function') {
        try {
          features.applyBbFromConfig({ rsSDK, options: _defaultOptions, appId: _defaultAppId });
        } catch (e) {
          if (_defaultOptions.debug) console.warn('[rsSDK] applyBbFromConfig failed', e);
        }
      }
      if (_mode === SDK_MODE.DEFAULT) {
        _autoCollect = resolveAutoCollect(_defaultOptions);
        if (_autoCollect.auto && (products.fp || products.di)) scheduleAutoCollect();
      }
      return;
    }
    if (command === 'collect') {
      let cb = typeof arg2 === 'function' ? arg2 : undefined;
      return runCollect(cb);
    }
    if (command === 'submit') {
      let cb = typeof arg2 === 'function' ? arg2 : undefined;
      return withCallback(runSubmit(), cb);
    }
    if (command === 'prewarm:hits' && typeof __BB_DEVTOOLS__ !== 'undefined' && __BB_DEVTOOLS__) {
      return getCollectPrewarmHits();
    }
    if (bbCommands && command === 'bb:collect') {
      let cb = typeof arg2 === 'function' ? arg2 : undefined;
      return bbCommands.collect(cb);
    }
    if (bbCommands && command === 'bb:debug' && bbCommands.debug) return bbCommands.debug();
  }

  function runCollect(callback) {
    if (typeof window === 'undefined') {
      if (_defaultOptions.debug) console.warn('[rsSDK] browser only');
      const r0 = { session_id: null, error: SDK_ERROR.BROWSER_ONLY };
      invokeCallback(callback, r0);
      return Promise.resolve(r0);
    }
    if (!_defaultAppId) {
      const r1 = { session_id: null, error: SDK_ERROR.MISSING_CONFIG };
      invokeCallback(callback, r1);
      return Promise.resolve(r1);
    }
    const products = resolveProducts(_defaultOptions);
    if (!products.fp && !products.di) {
      const nothing = { session_id: null, error: SDK_ERROR.NOTHING_TO_COLLECT };
      invokeCallback(callback, nothing);
      return Promise.resolve(nothing);
    }
    if (_collectInFlight) {
      const existing = _collectInFlight;
      if (callback) {
        existing.then(function (res) {
          invokeCallback(callback, res);
        });
      }
      return existing;
    }
    if (_autoCollectTimer) {
      clearTimeout(_autoCollectTimer);
      _autoCollectTimer = null;
    }
    const client = new RiskSealClient(_defaultAppId, _defaultOptions);
    const work = _mode === SDK_MODE.DEFAULT ? collectDeviceData(client) : client.collect();
    _collectInFlight = work.then(
      function (res) {
        _collectInFlight = null;
        _collectFailed = !res || !!res.error;
        invokeCallback(callback, res);
        return res;
      },
      function (err) {
        _collectInFlight = null;
        _collectFailed = true;
        const res = {
          session_id: null,
          error: errorCodeFrom(err, SDK_ERROR.COLLECT_FAILED),
        };
        invokeCallback(callback, res);
        return res;
      }
    );
    return _collectInFlight;
  }

  function startHeavyPrewarm() {
    const fp = resolveFpOptions(_defaultOptions);
    const products = resolveProducts(_defaultOptions);
    if (products.fp) {
      try {
        primeSpeechVoices();
      } catch (e) {}
    }
    if (products.fp || products.di) {
      try {
        startCollectPrewarm({
          prewarm: triState(fp.prewarm) !== false,
          storage: true,
          audio: products.fp,
          cpuBench: products.di && (triState(fp.cpuBenchPrewarm) ?? isAndroid()),
          render: triState(fp.renderPrewarm) ?? isAndroid(),
          renderNames: renderProbeNames(products),
        });
      } catch (e) {}
    }
  }

  function hasCachedDevice() {
    try {
      return hasFreshDeviceData(deviceScope(_defaultAppId, _defaultOptions));
    } catch (e) {
      return false;
    }
  }

  function scheduleAutoCollect() {
    _autoCollectTimer = setTimeout(function () {
      _autoCollectTimer = null;
      void runCollect();
    }, _autoCollect.delayMs);
  }

  function collectDeviceData(client) {
    return loadDeviceData(deviceScope(_defaultAppId, _defaultOptions)).then(function (cached) {
      if (cached) return { session_id: client.ensureSessionId() };
      startHeavyPrewarm();
      return client.collectToCache();
    });
  }

  function runSubmit(opts) {
    if (typeof window === 'undefined') {
      return Promise.resolve({ session_id: null, error: SDK_ERROR.BROWSER_ONLY });
    }
    if (!_defaultAppId) return Promise.resolve({ session_id: null, error: SDK_ERROR.MISSING_CONFIG });
    if (_mode !== SDK_MODE.DEFAULT) return Promise.resolve({ session_id: null, error: SDK_ERROR.UNSUPPORTED_MODE });
    const onDispatched = opts && typeof opts.onDispatched === 'function' ? opts.onDispatched : null;
    const client = new RiskSealClient(_defaultAppId, _defaultOptions);
    const behaviour = client.takeBehaviourSnapshot(bbCommands);
    const previous = _submitInFlight;
    if (previous && !behaviour.snapshot && !behaviour.error) {
      if (onDispatched) whenDispatched(onDispatched);
      return previous;
    }
    const previousGone = previous ? Promise.race([_submitDispatched, previous]) : null;
    let markDispatched = null;
    _submitDispatched = new Promise(function (resolve) {
      markDispatched = resolve;
    });
    if (onDispatched) whenDispatched(onDispatched);
    const run = previousGone
      ? previousGone.then(function () {
          return submitBehaviourOnly(client, behaviour, markDispatched);
        })
      : submitNow(client, behaviour, markDispatched);
    const current = run.then(
      function (res) {
        if (_submitInFlight === current) _submitInFlight = null;
        return res;
      },
      function (err) {
        if (_submitInFlight === current) _submitInFlight = null;
        return { session_id: null, error: errorCodeFrom(err, SDK_ERROR.SUBMIT_FAILED) };
      }
    );
    _submitInFlight = current;
    return current;
  }

  function whenDispatched(callback) {
    _submitDispatched.then(function () {
      setTimeout(callback, 0);
    });
  }

  function submitDispatch(onDispatched) {
    return { onDispatched, releaseAfterMs: bbCommands ? bbCommands.holdSubmitTimeoutMs() : 0 };
  }

  async function submitNow(client, behaviour, onDispatched) {
    const products = resolveProducts(_defaultOptions);
    const deviceRequested = products.fp || products.di;
    const fresh = deviceRequested ? client.readFreshPart() : null;
    let device = null;
    let deviceError = null;
    if (deviceRequested) {
      try {
        device = await deviceForSubmit();
      } catch (err) {
        deviceError = err;
      }
    }
    const dispatch = submitDispatch(onDispatched);
    return client.submit({ device, deviceError, deviceRequested, fresh, bb: bbCommands, behaviour, dispatch });
  }

  async function submitBehaviourOnly(client, behaviour, onDispatched) {
    const dispatch = submitDispatch(onDispatched);
    const storedDevice = behaviour.snapshot ? await storedDeviceOrNull() : null;
    return client.submit({
      device: null,
      deviceRequested: false,
      fresh: null,
      bb: bbCommands,
      behaviour,
      dispatch,
      storedDevice,
    });
  }

  function storedDeviceOrNull() {
    const products = resolveProducts(_defaultOptions);
    if (!products.fp && !products.di) return Promise.resolve(null);
    return loadDeviceData(deviceScope(_defaultAppId, _defaultOptions)).catch(function () {
      return null;
    });
  }

  async function deviceForSubmit() {
    const scope = deviceScope(_defaultAppId, _defaultOptions);
    const cached = await loadDeviceData(scope);
    if (cached) return cached;
    if (!_collectInFlight) {
      if (!_autoCollect.auto || _collectFailed) return null;
      void runCollect();
    }
    if (_collectInFlight) await withTimeout(_collectInFlight, SUBMIT_DEVICE_WAIT_MS, () => null);
    return loadDeviceData(scope);
  }

  function withCallback(promise, callback) {
    if (!callback) return promise;
    return promise.then(function (res) {
      invokeCallback(callback, res);
      return res;
    });
  }

  rsSDK.version = __SDK_VERSION__ ?? '';
  /** Built-in collect base URL (no trailing slash), from build-time `API_HOST`. Use in `endpoints: [custom, rsSDK.defaultEndpoint]`. */
  rsSDK.defaultEndpoint = String(config.defaultEndpoint).replace(/\/?$/, '');

  if (_rsSdkQueued.length) {
    for (let i = 0; i < _rsSdkQueued.length; i++) {
      const args = _rsSdkQueued[i];
      if (!args || !args.length) continue;
      const ret = rsSDK.apply(null, args);
      if (ret && typeof ret.catch === 'function') ret.catch(function () {});
    }
  }

  return rsSDK;
}
